import React, {useEffect, useState} from "react";
import {useListDocuments, useMutationSetDtLetturaMassiva} from "@commons/services/lavoratori/lavoratori.documenti.queries";
import {DocumentoParams} from "@commons/services/lavoratori/lavoratori.documenti.api";
import {DocType, MiniUniversalDoc, UniversalDocRecord} from "@commons/mapping/UniversalDocRecord";
import DTTable from "@components/dataTable/Table";
import * as Buttons from "@components/dataTable/buttons";
import moment from "moment";
import DTTitle from "@components/dataTable/TableTitle";
import Pagination from "@components/dataTable/Pagination";
import {usePagination} from "@components/dataTable/usePagination";
import {useClientSideProcessing} from "@components/dataTable/useClientSideProcessing";
import Loading from "@components/dataTable/Loading";
import TooltipEx from "@components/Tooltip";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectToken} from "@commons/core.slice";
import {QueryKey, useQueryClient} from "@tanstack/react-query";
import {DownloadFile, Preview} from "@components/dataTable/buttons/DownloadFile";
import {useMutationDownloadZIP} from "./Lavoratori.documenti.file-saver";
import {IconDownload, IconEyeOff, IconMailOpened} from "@tabler/icons-react";


interface SezioneDocsTableProps {
    anteprimaQueryKey: QueryKey,
    sezioneParams: DocumentoParams,
    isEmployee: boolean,
}

const SezioneDocsTable: React.FC<SezioneDocsTableProps> = ({sezioneParams, isEmployee, anteprimaQueryKey}) => {
    const [URI, setNextURI] = useState<string | null>(null);
    const token = useAppSelector(selectToken);

    const pagination = usePagination('',1, 10);
    const {page, setPage, pageLength, setPageLength, search, setSearch} = pagination;
    const sezioneDoc: DocType = sezioneParams.sezione as DocType;
    const {refetch, queryKey, isLoading, isFetching, isRefetching, error, data} = useListDocuments(sezioneParams);

    const queryClient = useQueryClient();
    const reloadSezione = () => {
        queryClient.invalidateQueries({queryKey: anteprimaQueryKey});
        queryClient.invalidateQueries({queryKey});
    }

    const docsSezione = useClientSideProcessing<UniversalDocRecord, keyof UniversalDocRecord>(data, pagination, ['anno', 'mese', 'tipoMensilita', 'descrizione']);

    const mutationSetDtLettura = useMutationSetDtLetturaMassiva(sezioneParams, reloadSezione);
    const mutationDownloadZIP = useMutationDownloadZIP(sezioneParams, reloadSezione);

    const [caricamentoInCorso, setCaricamentoInCorso] = useState<boolean>(false);
    const [toggleAll, setToggleAll] = useState<boolean>(false);
    const [docSelezionati, setDocSelezionati] = useState<MiniUniversalDoc[]>([]);

    let docSelezionatiLength = docSelezionati.length;
    let docsSezioneLength = docsSezione ? docsSezione.data.length : 0;

    function someDocsSelected() {
        if (toggleAll && docsSezioneLength > docSelezionatiLength) {
            return true
        }
        if (docsSezioneLength === docSelezionatiLength)
            return false
    }

    useEffect(() => {
        const nextDocSelezionati = [];
        let newObjDoc: MiniUniversalDoc = {dtLettura: null, idDoc: null, idFile: null, idPresaVisione: null, version: null};

        if (toggleAll && docsSezione)
            for (let doc of docsSezione.data) {
                newObjDoc = {dtLettura: doc.dtLettura, idDoc: doc.idDoc, idFile: doc.idFile, idPresaVisione: doc.idPresaVisione, version: doc.version};
                nextDocSelezionati.push(newObjDoc);
            }
        setDocSelezionati(nextDocSelezionati);
    }, [toggleAll]);

    const isSameDocs = (doc: MiniUniversalDoc, docToCheck: MiniUniversalDoc) => (doc.idDoc === docToCheck.idDoc && doc.idFile === docToCheck.idFile && doc.version === docToCheck.version)

    function isDocChecked(docToCheck: MiniUniversalDoc) {
        for (let doc of docSelezionati) {
            if (isSameDocs(doc, docToCheck)) {
                return true;
            }
        }
        return false
    }

    function toggleDocument(doc: UniversalDocRecord) {
        const isCheched = isDocChecked(doc);

        if (!isCheched) {
            const nextDocSelezionati = docSelezionati.map((x) => x);
            nextDocSelezionati.push(doc);
            setDocSelezionati(nextDocSelezionati);
        } else {
            const nextDocSelezionati = docSelezionati.filter((x) => !isSameDocs(x, doc));
            setDocSelezionati(nextDocSelezionati);
        }
    }

    function prepareList(soloNonLetti: boolean | undefined = false): MiniUniversalDoc[] {
        const nextPreparedList: MiniUniversalDoc[] = [];

        if (docsSezione)
            for (let doc of docSelezionati) {
                if (!soloNonLetti || doc.dtLettura === null || doc.dtLettura === undefined) {
                    const {idPresaVisione, idDoc, idFile, version, dtLettura} = doc;
                    nextPreparedList.push({idPresaVisione, idDoc, idFile, version, dtLettura});
                }
            }
        return nextPreparedList;
    }

    function segnaComeLetti() {
        setCaricamentoInCorso(true);
        mutationSetDtLettura.mutate(prepareList(true));
        setCaricamentoInCorso(false);
    }

    function downloadZip() {
        setCaricamentoInCorso(true);
        mutationDownloadZIP.mutate(prepareList());
        setCaricamentoInCorso(false);
    }

    return (<>
            <Loading loading={isLoading || isFetching || isRefetching || caricamentoInCorso}>

                <DTTitle id="elencoDocumentiLav" title="Elenco documenti" {...{currentPageLength: pageLength, setPageLength, currentSearch: search, setSearch}}/>

                <DTTable<UniversalDocRecord> dtSchema={docsSezione}
                                             header={<>
                                                 <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                     <label className="inline-flex min-w-max">
                                                         <input type="checkbox" id={'toggleAll_' + sezioneDoc}
                                                                className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${someDocsSelected() ? 'opacity-50' : ''}`}
                                                                onChange={(e) => {
                                                                    setToggleAll(e.target.checked)
                                                                }}
                                                         />&nbsp;Sel.
                                                     </label>
                                                 </th>
                                                 <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                     Descrizione
                                                 </th>
                                                 {sezioneDoc !== 'COMUNICAZIONI' && sezioneDoc !== 'DOC_PUBBLICI' && sezioneDoc !== 'ALTRI' &&
                                                     <>
                                                         <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                             Anno
                                                         </th>
                                                         {sezioneDoc !== 'CERTIFICAZIONI' &&
                                                             <>
                                                                 <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                                     Mese
                                                                 </th>
                                                                 <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                                     Tipo mensilit&agrave;
                                                                 </th>
                                                             </>
                                                         }</>
                                                 }
                                                 {sezioneDoc !== 'DOC_PUBBLICI' &&
                                                     <>
                                                         <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                                                             Dt pubblicazione
                                                         </th>
                                                         <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                             Dt lettura
                                                         </th>
                                                     </>
                                                 }
                                                 {sezioneDoc === 'DOC_PUBBLICI' &&
                                                     <>
                                                         <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">
                                                             Dt inizio
                                                         </th>
                                                         <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                             Dt fine
                                                         </th>
                                                     </>
                                                 }
                                                 <th scope="col" className="py-3.5 w-14"/>
                                             </>}

                                             rows={({item, index, isMobile, currentRow, register}) => (
                                                 <tr {...register(item.idFile!, index, item.URI === URI)}>
                                                     <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">

                                                         <input type="checkbox" id={"selectElement-" + index} name={"selectElement-" + index}
                                                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                                onClick={() => {
                                                                    toggleDocument(item);
                                                                }}
                                                                checked={isDocChecked(item)}
                                                                onChange={() => {
                                                                }}
                                                         />
                                                     </td>
                                                     <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                         <DescrizioneDoc item={item}/>
                                                     </td>
                                                     {sezioneDoc !== 'COMUNICAZIONI' && sezioneDoc !== 'DOC_PUBBLICI' && sezioneDoc !== 'ALTRI' &&
                                                         <>
                                                             <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                                 {item.anno}
                                                             </td>
                                                             {sezioneDoc !== 'CERTIFICAZIONI' &&
                                                                 <>
                                                                     <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                                         {item.mese}
                                                                     </td>
                                                                     <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                                         {item.tipoMensilita}
                                                                     </td>
                                                                 </>
                                                             }</>
                                                     }
                                                     {sezioneDoc !== 'DOC_PUBBLICI' &&
                                                         <>
                                                             <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 inline-flex">
                                                                 {item.dtPubblicazione && moment(item.dtPubblicazione).format('DD/MM/YYYY HH:mm:ss')}&nbsp;
                                                                 {(!item.visibleAzienda || !item.visible) && <TooltipEx tooltip={
                                                                     <>
                                                                         {!item.visibleAzienda && 'Documento non visibile né all\'azienda né al lavoratore'}
                                                                         {item.visibleAzienda && !item.visible && 'Documento non visibile al lavoratore'}
                                                                     </>
                                                                 }>
                                                                     <div className={`p-0 m-0 ${!item.visibleAzienda && 'text-red-600'}`}><IconEyeOff size={20}/></div>
                                                                 </TooltipEx>}
                                                             </td>

                                                             <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                                 {item.dtLettura && moment(item.dtLettura).format('DD/MM/YYYY HH:mm:ss')}
                                                             </td>
                                                         </>
                                                     }
                                                     {sezioneDoc === 'DOC_PUBBLICI' &&
                                                         <>
                                                             <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                                 {item.inizioValidita && moment(item.inizioValidita).format('DD/MM/YYYY HH:mm:ss')}
                                                             </td>
                                                             <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                                 {item.fineValidita && moment(item.fineValidita).format('DD/MM/YYYY HH:mm:ss')}
                                                             </td>
                                                         </>
                                                     }
                                                     <td className="whitespace-nowrap py-2 text-sm font-medium">
                                                         <Buttons.Visibility visible={isMobile || index === currentRow}>
                                                             <Preview srcFile={item.URI} onFulfilled={reloadSezione}/>
                                                             <DownloadFile srcFile={item.URI} onFulfilled={reloadSezione}/>
                                                         </Buttons.Visibility>
                                                     </td>
                                                 </tr>
                                             )
                                             }/>

                <Pagination dataTables={docsSezione} currentPage={page} setPage={setPage} currentPageLength={pageLength}/>

                <div className="w-full text-right pt-5">
                    {isEmployee && sezioneDoc !== 'DOC_PUBBLICI' &&
                        <button type="button" className="bg-blue-600 btnPrimary inline-flex mx-2" onClick={() => segnaComeLetti()}
                                disabled={docSelezionatiLength === 0 || docsSezioneLength === 0}>
                            <IconMailOpened className="h-5 w-5"/>&nbsp;Segna come letto
                        </button>}

                    <button type="button" className="bg-blue-600 btnPrimary inline-flex mx-2" formTarget="_blank" onClick={() => downloadZip()}
                            disabled={docSelezionatiLength === 0 || docsSezioneLength === 0}>
                        <IconDownload className="h-5 w-5"/>&nbsp;Scarica
                    </button>
                </div>
            </Loading>
        </>
    )
}

export default SezioneDocsTable;

export const DescrizioneDoc: React.FC<{ item: UniversalDocRecord }> = ({item}) => {
    return <TooltipEx tooltip={
        item.descrizioneEstesa ?
            item.descrizioneEstesa.map((line, index) =>
                <p key={index}>{line}</p>)
            : null
    }>
        {item.descrizione}
    </TooltipEx>
}